/** @jsx jsx */
import { jsx } from '@emotion/core'
import { HFlow, Icon, Text, VFlow } from 'bold-ui'
import { css } from 'emotion'
import { useMedicamentoUsoBarraLateralQuery, usePossuiTratamentoConcluidoQuery } from 'graphql/hooks.generated'
import React, { useMemo } from 'react'

import { AccordionCardSection } from '../components/AccordionCardSection'
import { ContentCardSection } from '../components/ContentCardSection'
import quebraListaBarraLateral from '../util/quebraListaBarraLateral'

export interface MedicamentosCardProps {
  prontuarioId: ID
  dataAtendimento: Instant
}

export function MedicamentosCard(props: MedicamentosCardProps) {
  const { prontuarioId, dataAtendimento } = props
  const {
    data: { listaMedicamento },
    loading,
  } = useMedicamentoUsoBarraLateralQuery({
    variables: {
      input: {
        prontuarioId,
        dataAtendimento,
        usoContinuo: false,
        ativo: true,
      },
    },
  })

  const {
    data: { possuiTratamentoConcluido },
    loading: loadingTratamentoConcluido,
  } = usePossuiTratamentoConcluidoQuery({
    variables: { input: { prontuarioId, dataAtendimento } },
    skip: listaMedicamento?.content.length !== 0,
  })

  const medicamentosEmUso = useMemo(() => quebraListaBarraLateral(listaMedicamento?.content ?? []), [listaMedicamento])
  const medicamentosEmUsoExpandido = medicamentosEmUso.listaExpandida
  const medicamentosEmUsoAccordion = medicamentosEmUso.listaAccordion

  const itemMedicamentoEmUso = (medicamentoEmUso) => (
    <HFlow key={medicamentoEmUso.id} hSpacing={0.5} alignItems='center'>
      <Icon icon='medicineBottleFilled' fill='primary' />
      <VFlow
        vSpacing={0.2}
        style={css`
          word-break: break-word;
        `}
      >
        <Text fontWeight='bold'>
          {`${medicamentoEmUso.medicamento.principioAtivo} ${medicamentoEmUso.medicamento.concentracao ?? ''}`.trim()}
        </Text>
        <Text>{medicamentoEmUso.posologia}</Text>
      </VFlow>
    </HFlow>
  )

  return (
    <React.Fragment>
      <ContentCardSection isLoading={loading && loadingTratamentoConcluido}>
        {listaMedicamento?.content.length !== 0 ? (
          <VFlow vSpacing={0.5}>
            {medicamentosEmUsoExpandido?.map((medicamento) => itemMedicamentoEmUso(medicamento))}
          </VFlow>
        ) : (
          <Text component='p' fontStyle='italic'>
            {possuiTratamentoConcluido
              ? 'Nenhum medicamento em uso. Ver medicamentos com tratamento concluído.'
              : 'Nenhum medicamento registrado.'}
          </Text>
        )}
      </ContentCardSection>
      {medicamentosEmUsoAccordion.length > 0 && (
        <AccordionCardSection
          id='MedicamentosCard'
          title={{
            closed: `Mostrar + ${medicamentosEmUsoAccordion.length} medicamentos`,
            open: `Esconder + ${medicamentosEmUsoAccordion.length} medicamentos`,
          }}
          tooltip={{ closed: 'Expandir medicamentos', open: 'Esconder medicamentos' }}
        >
          <ContentCardSection>
            <VFlow vSpacing={0.5}>
              {medicamentosEmUsoAccordion?.map((medicamento) => itemMedicamentoEmUso(medicamento))}
            </VFlow>
          </ContentCardSection>
        </AccordionCardSection>
      )}
    </React.Fragment>
  )
}
